import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Spin } from "antd";
import { Eye, EyeOff } from 'lucide-react';
import { notifyUser } from "../../../util/util";
import { extractErrorMessage } from '../../../hooks/extractErrorMessage';
import { ReactComponent as LockIcon } from '../../../assets/UiElements/lock.svg';
import { useResetPassword } from '../../../hooks/useAuth';

export const CreatePasswordFP = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const token = location.state?.token || sessionStorage.getItem('reset_token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [mounted, setMounted] = useState(false);
    // const [loading, setLoading] = useState(false);

    // Delay rendering of omk-text-field until after mount
    useEffect(() => {
        setMounted(true);
    }, []);

    // Removed unused togglePasswordType function

    // Removed unused toggleConfirmPasswordType function

    // ...removed unused toggleLoading...
    const isButtonDisabled = !confirmPassword || !password;

    const criteria = {
        minLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        hasNumber: /\d/.test(password),
        noLeadingSpecial: !/^[^a-zA-Z0-9]/.test(password),
    };

    const resetPasswordMutation = useResetPassword();
    const createPassword = () => {
        if (!token) {
            notifyUser('error', 'Session expired. Please try again.', 'Error');
            navigate('/forgot-password');
            return;
        }
        const isValid = Object.values(criteria).every(Boolean);
        if (isValid) {
            if (password !== confirmPassword) {
                notifyUser('error', 'Password must match password confirmation', 'Validation Error');
                return;
            }
            resetPasswordMutation.mutate(
                { data: { password, c_password: confirmPassword }, token },
                {
                    onSuccess: (response) => {
                        if (response.data?.success) {
                            sessionStorage.removeItem('reset_token');
                            navigate('/forgot-password/success');
                        } else {
                            notifyUser('error', extractErrorMessage(response, 'Password reset failed'), 'Error');
                        }
                    },
                    onError: (error) => {
                        notifyUser('error', extractErrorMessage(error, 'Password reset failed'), 'Error');
                    }
                }
            );
        } else {
            notifyUser('error', 'Password must match all requirements', 'Validation Error');
        }
    };

    return (
        <div className="mx-auto mobile-full-width shadow bg-white p-5 rounded login-width">
            <div align="center">
                <p className="lead fw-bold">Create your password</p>
                <small className="text-center">Create a strong & secure password to ensure your account is safe online.</small>

                <Spin spinning={resetPasswordMutation.isPending}>
                    <div className="mt-4 w-75 mx-auto mobile-full-width">
                        <div className="mb-4 position-relative">
                            {mounted && (
                                <omk-text-field
                                    label="Password"
                                    value={password}
                                    type={showPassword ? "text" : "password"}
                                    className="mb-4"
                                    onInput={e => setPassword(e.target.value)}
                                    name="password"
                                />
                            )}
                            <button
                                type="button"
                                className="btn btn-link position-absolute"
                                style={{
                                    right: '15px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'none',
                                    zIndex: 10
                                }}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={20} color="#666" /> : <Eye size={20} color="#666" />}
                            </button>
                        </div>

                        {/* Conditionally render the password criteria */}
                        {password && (
                            <div className="row mb-4">
                                <div className="col-6">
                                    <small className="float-start">
                                        {criteria.minLength ? (
                                            <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                                        ) : (
                                            <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                                        )}
                                        Min 8 characters
                                    </small><br />
                                    <small className="float-start">
                                        {criteria.hasUppercase ? (
                                            <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                                        ) : (
                                            <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                                        )}
                                        An uppercase
                                    </small><br />
                                    <small className="float-start">
                                        {criteria.noLeadingSpecial ? (
                                            <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                                        ) : (
                                            <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                                        )}
                                        Must not start with a special character
                                    </small>
                                </div>
                                <div className="col-6">
                                    <small className="float-start">
                                        {criteria.hasSpecialChar ? (
                                            <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                                        ) : (
                                            <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                                        )}
                                        A special character
                                    </small><br />
                                    <small className="float-start">
                                        {criteria.hasNumber ? (
                                            <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                                        ) : (
                                            <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                                        )}
                                        A number
                                    </small>
                                </div>
                            </div>
                        )}

                        <div className="mb-5 position-relative">
                            <omk-text-field
                                label="Confirm Password"
                                value={confirmPassword}
                                type={showConfirmPassword ? "text" : "password"}
                                onInput={e => setConfirmPassword(e.target.value)}
                            >
                               
                            </omk-text-field>
                            <button
                                type="button"
                                className="btn btn-link position-absolute"
                                style={{
                                    right: '15px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'none',
                                    zIndex: 10
                                }}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <EyeOff size={20} color="#666" /> : <Eye size={20} color="#666" />}
                            </button>
                        </div>

                      {/*  <p
                            className="text-center omk-text-small fw-bold"
                            onClick={() => navigate('/onboard/needhelp')}
                            style={{ textDecoration: 'underline' }}
                        >
                            Need help?
                        </p>*/}

                        <p className="text-center omk-text-small text-black-50">
                            <LockIcon style={{ marginRight: '5px', width: '16px', height: '16px' }} />
                            Your information is confidential and secure
                        </p>

                        <button
                            className={`btn ${isButtonDisabled ? 'btn-secondary' : 'btn-primary'} mb-4 w-50 mobile-full-width`}
                            disabled={isButtonDisabled}
                            style={{
                                borderRadius: '50px',
                                padding: isButtonDisabled ? '5px 20px' : '10px 20px',
                                height: 'auto',
                            }}
                            onClick={createPassword}
                        >
                            Continue
                        </button>
                    </div>
                </Spin>
            </div>
        </div>
    );
};
